import PT from 'prop-types';
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import { Mention, MentionsInput } from 'react-mentions';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import CharacterLimit from '~/ui/components/CharacterLimit/CharacterLimit';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { CHARACTER_INPUT_LIMIT_SHORT } from '~/ui/enums/input';
import { sortByStringValue } from '~/utils/array';
import { capitalize } from '~/utils/strings';
import { getUserName } from '~/utils/user';

import mentionStyles from './DescriptionTextAreaForm.Mentions.style';

function DescriptionTextAreaForm({
  description: initialDescription,
  disabled = false,
  id,
  limit = CHARACTER_INPUT_LIMIT_SHORT,
  title: titleProp,
  onUpdate,
  userMentionsData,
  onMentionUser,
  mentionInputRef,
  classes,
}) {
  const intl = useIntl();
  const formatNumber = useFormatNumber();

  const title =
    titleProp ||
    intl.formatMessage({
      id: 'DescriptionTextAreaForm.title',
      defaultMessage: 'description',
      description: 'Default Title for description text area',
    });
  const description = initialDescription || '';
  const [editMode, setEditMode] = useState(description !== '');

  function renderUserSuggestion({ id }) {
    const user = userMentionsData[id];
    return getUserName(user, intl);
  }

  function displayTransform(id) {
    const user = userMentionsData[id];
    return getUserName(user, intl);
  }

  function handleRemoveDescription() {
    onUpdate('');
    setEditMode(false);
  }

  function mapUsersToMentionsData() {
    return Object.values(userMentionsData)
      .map((user) => ({
        id: user.id,
        display: getUserName(user, intl),
      }))
      .sort(sortByStringValue('display'));
  }

  function renderDescription() {
    return (
      <Box py={2}>
        <FlexLayout space={4} alignItems="center">
          <Icon color="status.info" icon="field-paragraph" />
          <Text preset="description">{capitalize(title)}</Text>
        </FlexLayout>
        <FlexLayout
          py={2}
          alignItems="top"
          justifyContent="space-between"
          space={2}
        >
          <FlexLayout flexDirection="column" flexGrow="1">
            {renderInputComponent()}
          </FlexLayout>
          <FlexLayout space={4}>
            <IconButton
              text={intl.formatMessage({
                id: 'DescriptionTextAreaForm.clearText',
                defaultMessage: 'Clear text',
                description: 'Tooltip for clear text button',
              })}
              icon="trash"
              onClick={handleRemoveDescription}
              id="clearText"
            />
          </FlexLayout>
        </FlexLayout>
      </Box>
    );
  }

  function renderInputComponent() {
    if (userMentionsData) {
      return (
        <Box sx={{ width: '100%' }}>
          <MentionsInput
            data-testid="textArea"
            value={description}
            onChange={(_, newValue) => onUpdate(newValue)}
            className={classes.mentionsInput}
            inputRef={mentionInputRef}
          >
            <Mention
              data={mapUsersToMentionsData()}
              markup="{{user:__id__}}"
              trigger="@"
              displayTransform={displayTransform}
              renderSuggestion={renderUserSuggestion}
              className={classes.mention}
              onAdd={(id) => onMentionUser(id)}
            />
          </MentionsInput>
          <FlexLayout justifyContent="flex-end">
            {limit && <CharacterLimit limit={limit} value={description} />}
          </FlexLayout>
        </Box>
      );
    } else {
      return (
        <FormField
          input={TextArea}
          inputProps={{
            onChange: (value) => onUpdate(value),
            maxLength: limit,
          }}
          value={description || ''}
          footer={intl.formatMessage(
            {
              id: 'DescriptionTextAreaForm.characterCountFooter',
              defaultMessage: '{currentLength}/{maxLength} characters',
              description:
                'Footer for description textarea. Example: currentLength = 10, maxLength = 100',
            },
            {
              currentLength: formatNumber(description.length),
              maxLength: formatNumber(limit),
            },
          )}
        />
      );
    }
  }

  return disabled ? (
    renderDescription()
  ) : description === '' && !editMode ? (
    <FlexLayout id={id} space={4} py={2} onClick={() => setEditMode(true)}>
      <Icon color="status.active" icon="field-paragraph" />
      <Text color="status.active">
        {intl.formatMessage(
          {
            id: 'DescriptionTextAreaForm.addTitle',
            defaultMessage: 'Add {title}',
            description:
              'Button text to add description or other content. Example: title = "description", "notes"',
          },
          { title },
        )}
      </Text>
    </FlexLayout>
  ) : (
    renderDescription()
  );
}

DescriptionTextAreaForm.propTypes = {
  description: PT.string,
  disabled: PT.bool,
  onUpdate: PT.func,
  userMentionsData: PT.shape({
    id: PT.number,
    firstname: PT.string,
    lastName: PT.string,
    email: PT.string,
  }),
  onMentionUser: PT.func,
  mentionInputRef: PT.object,
};

export default injectSheet(mentionStyles)(DescriptionTextAreaForm);
